import store from '@/store'
import { constantStrings, attachmentTypes, apiErrors, chatEvents, feedback } from '@/services/constants'
import agentDeskProperties from "../constants/agentDeskProperties"
import { GlobalVars } from '@/services/globals'
import AppLog from '@/services/errors/app-logs'
import AppWarn from '@/services/errors/app-warns'
import { getMaxTimeForTypingIndicator, getMinTimeForTypingIndicator, updateUnreadIndicator, getTranslation } from '@/services/utils'
import Storage from '@/services/storageLib'
import { setCookie, getCookie } from '@/services/cookie'

function captureInfoLogs(event, message, isConsole=true) {
  const enableZendeskConsoleLog = fetchAgentDeskConfig()?.enableZendeskConsoleLog
  const enableZendeskSentryLog = fetchAgentDeskConfig()?.enableZendeskSentryLog
  enableZendeskConsoleLog && isConsole && console.log(`Zendesk INFO: ${event}: ${message}`)
  enableZendeskSentryLog && new AppLog({name: constantStrings.ZENDESK, message:`${event}: ${message}`, logLevel: 'info', service: 'LIVEAGENT'})
}

function captureWarnLogs(event, message) {
  const enableZendeskErrorLog = fetchAgentDeskConfig()?.enableZendeskErrorLog
  if (enableZendeskErrorLog === false) {
    return
  }
  const name = constantStrings.ZENDESK
  console.log(`Zendesk ERROR: ${event}: ${message}`)
  new AppWarn({name, message:`${event}: ${message}`, service: 'LIVEAGENT'})
}

function captureSuccessLogs(event, message) {
  const enableZendeskConsoleLog = fetchAgentDeskConfig()?.enableZendeskConsoleLog
  const enableZendeskSentryLog = fetchAgentDeskConfig()?.enableZendeskSentryLog
  enableZendeskConsoleLog && console.log(`${constantStrings.ZENDESK.concat(' ', apiErrors.SUCCESS)}: ${event}: ${message}`)
  enableZendeskSentryLog && new AppLog({name: constantStrings.ZENDESK, message:`${event}: ${message}`, logLevel: 'info', service: 'LIVEAGENT'})
}

function loadAgentDeskSDK(agentDeskName, onLoadHandler) {
  // For zendesk we have sdk locally
  // For other agentdesks, sdk can be loaded through script as well
  if (agentDeskName === constantStrings.ZENDESK) {
    return onLoadHandler()
  } else {
    // Reusable code for future agent desk integrations
    /* let agentDeskSDKTag = document.getElementById(agentDeskProperties.SCRIPT_TAG_ID)
    if (agentDeskSDKTag) {
      return onLoadHandler()
    }
    const NETOMI_SDK_CDN_DOMAIN = process.env.NETOMI_SDK_CDN_DOMAIN
    const s3BucketUrl =(
      NETOMI_SDK_CDN_DOMAIN === '' ? 
       'https://chatwidgetprod.netomi.com' 
       : new URL(process.env.NETOMI_SDK_CDN_DOMAIN).origin
    )
    agentDeskSDKTag = document.createElement('script')
    agentDeskSDKTag.setAttribute('id', agentDeskProperties.SCRIPT_TAG_ID)
    agentDeskSDKTag.setAttribute('type','text/javascript')
    captureInfoLogs('Fetching ZD SDK from S3', `URL: ${s3BucketUrl}, DOMAIN: ${NETOMI_SDK_CDN_DOMAIN}`)
    agentDeskSDKTag.setAttribute('src', `${s3BucketUrl}/zd-sdk.js`)
    document.head.appendChild(agentDeskSDKTag)
    agentDeskSDKTag.addEventListener('load', onLoadHandler) */
  }
}

function sendMessagePayloadFormatter(message, timestamp, hideMessage = true, isEvent = false) {
  new AppLog({
    name: 'NET-57075 sendMessagePayloadFormatter', 
    message: `Called with params: message=${message}, timestamp=${timestamp}, hideMessage=${hideMessage}, isEvent=${isEvent}`, 
    logLevel: 'info', 
    service: 'Action'
  })
  return {
    userMessage: {
      payload: message,
      label: constantStrings.BACK_TO_BOT_SYSTEM_EVENT,
      timestamp,
      hideMessage
    },
    ...(isEvent) && {messageType: constantStrings.INFO}
  }
}

function generateMessageSeperator(type, message, timestamp) {
  GlobalVars.$liveAgent.showMessageSeperator(type, message, timestamp)
  updateUnreadIndicator(true)
}

const genericAgentDeskResponseHandlers = {
  responseTypeEventHandler(eventData, response) {
    response.eventType = eventData.values[0]
  },
  agentNameEventHandler(eventData, response) {
    if (eventData?.values?.['0']?.length) {
      response.displayName = eventData.values[0]
    }
  },
  agentUrlEventHandler(eventData, response) {
    response.avatar = eventData?.values?.['0'] || `${GlobalVars.$cdn}/images/agent_default.svg`
  },
  queuePositionEventHandler(eventData,  response) {
    response.queuePosition = parseInt(eventData.values[0])
  }
}

function genericAgentDeskResponseMapper () {
  const { EVENT_TYPE, AGENT_NAME, AGENT_AVATAR_URL, QUEUE_POSITION } = agentDeskProperties[constantStrings.GENERIC_AGENTDESK].EVENT_INFO
  return {
    [EVENT_TYPE]: genericAgentDeskResponseHandlers.responseTypeEventHandler.bind(this),
    [AGENT_NAME]: genericAgentDeskResponseHandlers.agentNameEventHandler.bind(this),
    [AGENT_AVATAR_URL]: genericAgentDeskResponseHandlers.agentUrlEventHandler.bind(this),
    [QUEUE_POSITION]: genericAgentDeskResponseHandlers.queuePositionEventHandler.bind(this)
  }
}

function getGenericAgentDeskResponseInfo(customFields) {
  let response = {}
  customFields.forEach(field => {
    const fieldTypeMapper = genericAgentDeskResponseMapper()[field.name]
    if (fieldTypeMapper) {
      fieldTypeMapper(field, response)
    }
  })
  return response
}

function checkToDisplayQueuePosition(oldQueuePosition, newQueuePosition) {
  if (!newQueuePosition || newQueuePosition <= 0) {
    return false
  }
  else if (!oldQueuePosition || oldQueuePosition !== newQueuePosition) {
    return true
  }
  return false
}

function checkLiveAgentDeskName(agentDesk) {
  return store.state.widgetConfig?.liveAgentDeskConfig?.agentDeskChannelName === agentDesk
}

function checkHumanHandoffTriggered(ownerType) {
  return ownerType === constantStrings.AGENT && store.state.controlType === constantStrings.BOT
}

function checkBackToBotTriggered(ownerType) {
  return ownerType === constantStrings.BOT && store.state.controlType === constantStrings.AGENT
}

function extractPayload(requestBody) {
  let type = attachmentTypes.Text
  if (requestBody.attachmentList) {
    return requestBody.attachmentList?.reduce((responseTillNow, responseCurr) => {
      let message
      if (responseCurr?.type === attachmentTypes.Image && responseCurr.largeImageUrl) {
        message = `${responseCurr.title}: ${responseCurr.largeImageUrl}\n`
        type = attachmentTypes.Image
      }else if (responseCurr?.type === attachmentTypes.File && responseCurr.fileURL) {
        message = `${responseCurr.title}: ${responseCurr.fileURL}\n`
        type = attachmentTypes.File
      }
      if (message) {
        responseTillNow += message
      }
      return { message: responseTillNow, type }
    }, '')
  }else {
    return { message: requestBody.messagePayload.label, type }
  }
}

function fetchAgentDeskConfig() {
  const currBotRefId = store.state.botRefId
  const botRefIdSandbox = store.state.widgetConfig?.botRefIdSandbox
  const liveAgentDeskConfigSandbox = store.state.widgetConfig?.liveAgentDeskConfigSandbox
  const liveAgentDeskConfig = store.state.widgetConfig?.liveAgentDeskConfig

  if (checkLiveAgentDeskName(constantStrings.ZENDESK)) {
    if (currBotRefId === botRefIdSandbox && liveAgentDeskConfigSandbox) {
      return liveAgentDeskConfigSandbox
    }
  }
  return liveAgentDeskConfig
}

const setTimeOutForTypingIndicator = () => {
  let setTimeOutId = ''
  const startTimer = () => {
    return (() => {
      const maxTimeForTypingIndicator = getMaxTimeForTypingIndicator()
      const minTimeForTypingIndicator = getMinTimeForTypingIndicator()
      const typingIndicatorTimeoutDelay = maxTimeForTypingIndicator + minTimeForTypingIndicator
      setTimeOutId = setTimeout(() => {
        store.dispatch('updateTypingIndicatorStatus', false)
      }, typingIndicatorTimeoutDelay)
    })()
  }
  const clearTimer = () => {
    return (() => {
      clearTimeout(setTimeOutId)
    })()
  }
  return {
    startTimer,
    clearTimer
  }
}

const handleIfAuthKeyIsInvalid = async () => {
  new AppLog({
        name: 'NET-57075 handleIfAuthKeyIsInvalid', 
        message: 'Auth key invalid - triggering back to bot', 
        logLevel: 'info', 
        service: 'Action'
      })
  await store.dispatch('sendAgentEventInfo', { ...sendMessagePayloadFormatter(agentDeskProperties.BACK_TO_BOT_UTTERANCE), agentDeskHelperMessage: true })
}

const getAgentAvatarFromAgentId = (agentId) => {
  const agentInfoArray = Storage.getLocalStorageData(agentDeskProperties.AGENT_INFO)
  const agentInfo = agentInfoArray.find(agentInfo => agentInfo.agentId === agentId)
  return agentInfo?.agentAvatar
}

const addAgentInfoInLocalStorage = (newAgentInfo) => {
  const agentInfoArray = Storage.getLocalStorageData(agentDeskProperties.AGENT_INFO) || []
  agentInfoArray.push(newAgentInfo)
  Storage.setLocalStorageData(agentDeskProperties.AGENT_INFO, agentInfoArray)
}

const updateAgentInfoInLocalStorage = ({ displayName, avatar }) => {
  const agentInfoArray = Storage.getLocalStorageData(agentDeskProperties.AGENT_INFO)
  const agentInfoIndex = agentInfoArray?.findIndex(agentInfo => agentInfo.agentName === displayName)
  agentInfoArray[agentInfoIndex].agentAvatar = avatar
  Storage.setLocalStorageData(agentDeskProperties.AGENT_INFO, agentInfoArray)
}

const removeAgentInfoInLocalStorage = (agentId) => {
  if (!agentId) return
  const currentAgentInfo = Storage.getLocalStorageData(agentDeskProperties.AGENT_INFO) || []
  const updatedAgentInfo = currentAgentInfo.filter(agentInfo => agentInfo.agentId !== agentId)
  Storage.setLocalStorageData(agentDeskProperties.AGENT_INFO, updatedAgentInfo)
}

const isTranscriptValid = () => {
  const tabId = Storage.getSessionStorageData('tabId')
  const data = getCookie(agentDeskProperties.TRANSCRIPT_RELATED_INFO_KEY)
  if (!data || tabId === data) {
    setCookie(agentDeskProperties.TRANSCRIPT_RELATED_INFO_KEY, tabId)
    return true
  }
  return false
}

const handlePillInfoEvents = {
    handleAgentJoinPill ({ agentName = 'Agent'}) {
      return `${agentName} ${getTranslation('HAS_JOINED_THE_CHAT')}`
    },
    handleAgentLeavePill ({ agentName = 'Agent'}) {
      return `${agentName} ${getTranslation('HAS_LEFT_THE_CHAT')}`
    },
    handleAgentTransferPill ({ agentName, previousAgentName = 'Agent', departmentName}) {
      return `${previousAgentName} ${getTranslation('HAS_TRANSFERRED_THE_CHAT')} ${(agentName || !departmentName) ? agentName || 'Agent' : departmentName}`
    },
    handleAgentTransferAndJoinPill ({ agentName = 'Agent', previousAgentName = 'Agent'}) {
      return `${previousAgentName} ${getTranslation('HAS_TRANSFERRED_THE_CHAT')} ${agentName}`
    },
    handleAgentWaitPill ({ queuePosition }) {
      return `${getTranslation('QUEUE_POSITION')} ${queuePosition}`
    },
    handlePillInfo ({ pillInfo }) {
      return pillInfo
    },
    handleSignedIn() {
      return `Successfully signed in!`
    },
    handleSignedOut() {
      return `Successfully signed out!`
    },
    handleChatEnd () {
      return constantStrings.CHAT_ENDED
    },
    handleSessionExpire () {
      return constantStrings.SESSION_HAS_EXPIRED
    },
    handleCSATSurvey ({ feedbackValue, submitSurveyInfo, hideComment }) {
      const rating = feedbackValue === feedback.POSITIVE ? constantStrings.CHAT_RATED_GOOD : feedbackValue === feedback.NEGATIVE ? constantStrings.CHAT_RATED_BAD : constantStrings.CHAT_RATING_REMOVED
      if (!submitSurveyInfo) {
        return rating
      }
      const additionalFeedback = submitSurveyInfo.additionalFeedback || ''
      return hideComment ? constantStrings.SURVEY_SUBMITTED : `${constantStrings.SURVEY_SUBMITTED}: ${additionalFeedback}`
    },
    handleGenericPill ({ messageInfo }) {
      return messageInfo
    }
}

const getPillInfo = (message) => {
  const { AGENT_JOIN, AGENT_JOIN_CONFERENCE, AGENT_LEFT_CONFERENCE, AGENT_LEAVE, AGENT_TRANSFER, AGENT_TRANSFER_AND_JOIN, WAIT, CONFIG, AGENTDESK_CONFIGURATION_NOT_FOUND, GENERIC_INFO } = agentDeskProperties.EVENT_SUB_TYPES
  const { SUBMIT } = chatEvents
  return {
    [AGENT_JOIN]: handlePillInfoEvents.handleAgentJoinPill.bind(message),
    [AGENT_JOIN_CONFERENCE]: handlePillInfoEvents.handleAgentJoinPill.bind(message),
    [AGENT_LEFT_CONFERENCE]: handlePillInfoEvents.handleAgentLeavePill.bind(message),
    [AGENT_LEAVE]: handlePillInfoEvents.handleAgentLeavePill.bind(message),
    [AGENT_TRANSFER]: handlePillInfoEvents.handleAgentTransferPill.bind(message),
    [AGENT_TRANSFER_AND_JOIN]: handlePillInfoEvents.handleAgentTransferAndJoinPill.bind(message),
    [WAIT]: handlePillInfoEvents.handleAgentWaitPill.bind(message),
    [GENERIC_INFO]: handlePillInfoEvents.handleGenericPill.bind(message),
    [CONFIG]: handlePillInfoEvents.handlePillInfo.bind(message),
    [AGENTDESK_CONFIGURATION_NOT_FOUND]: handlePillInfoEvents.handlePillInfo.bind(message),
    SESSION_EXPIRE: handlePillInfoEvents.handleSessionExpire,
    CHAT_END: handlePillInfoEvents.handleChatEnd,
    SIGN_IN:  handlePillInfoEvents.handleSignedIn,
    SIGN_OUT: handlePillInfoEvents.handleSignedOut,
    [SUBMIT]: handlePillInfoEvents.handleCSATSurvey,
  }
}

export {
    setTimeOutForTypingIndicator,
    loadAgentDeskSDK,
    sendMessagePayloadFormatter,
    checkHumanHandoffTriggered,
    checkBackToBotTriggered,
    checkLiveAgentDeskName,
    getGenericAgentDeskResponseInfo,
    generateMessageSeperator,
    checkToDisplayQueuePosition,
    captureWarnLogs,
    captureInfoLogs,
    captureSuccessLogs,
    fetchAgentDeskConfig,
    extractPayload,
    handleIfAuthKeyIsInvalid,
    addAgentInfoInLocalStorage,
    updateAgentInfoInLocalStorage,
    removeAgentInfoInLocalStorage,
    getAgentAvatarFromAgentId,
    isTranscriptValid,
    getPillInfo
}
